import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  getTrip,
  getMyRole,
  listTripMembersWithEmail,
  updateMemberRole,
  removeMember,
  enableTripSharing,
  disableTripSharing,
} from '@/services/tripService'
import {
  inviteTripMember,
  listPendingInvitations,
  revokeInvitation,
} from '@/services/invitationService'
import { useAuthStore } from '@/store/authStore'
import { inviteMemberSchema, INVITATION_ROLES, type InviteMemberFormValues } from '@/utils/schemas'

export function MembersPage() {
  const { t } = useTranslation()
  const { tripId } = useParams<{ tripId: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const currentUserId = useAuthStore((state) => state.user?.id)
  const [confirmRemoveUserId, setConfirmRemoveUserId] = useState<string | null>(null)
  const [confirmRevokeId, setConfirmRevokeId] = useState<string | null>(null)
  const [linkCopied, setLinkCopied] = useState(false)

  const tripQuery = useQuery({
    queryKey: ['trip', tripId],
    queryFn: () => getTrip(tripId!),
    enabled: !!tripId,
  })

  const myRoleQuery = useQuery({
    queryKey: ['my-role', tripId],
    queryFn: () => getMyRole(tripId!),
    enabled: !!tripId,
  })

  const canManage = myRoleQuery.data === 'owner' || myRoleQuery.data === 'companion'

  const membersQuery = useQuery({
    queryKey: ['trip-members', tripId],
    queryFn: () => listTripMembersWithEmail(tripId!),
    enabled: !!tripId,
  })

  const invitationsQuery = useQuery({
    queryKey: ['trip-invitations', tripId],
    queryFn: () => listPendingInvitations(tripId!),
    enabled: !!tripId && canManage,
  })

  const invalidateAll = async () => {
    await queryClient.invalidateQueries({ queryKey: ['trip-members', tripId] })
    await queryClient.invalidateQueries({ queryKey: ['trip-invitations', tripId] })
    await queryClient.invalidateQueries({ queryKey: ['trip-member-count', tripId] })
  }

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<InviteMemberFormValues>({
    resolver: zodResolver(inviteMemberSchema),
    defaultValues: { role: 'companion' },
  })

  const inviteMutation = useMutation({
    mutationFn: (values: InviteMemberFormValues) =>
      inviteTripMember(tripId!, values.email, values.role),
    onSuccess: async () => {
      await invalidateAll()
      reset({ email: '', role: 'companion' })
    },
  })

  const updateRoleMutation = useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: 'companion' | 'viewer' }) =>
      updateMemberRole(tripId!, userId, role),
    onSuccess: invalidateAll,
  })

  const removeMutation = useMutation({
    mutationFn: (userId: string) => removeMember(tripId!, userId),
    onSuccess: async () => {
      await invalidateAll()
      setConfirmRemoveUserId(null)
    },
  })

  const revokeMutation = useMutation({
    mutationFn: (invitationId: string) => revokeInvitation(invitationId),
    onSuccess: async () => {
      await invalidateAll()
      setConfirmRevokeId(null)
    },
  })

  const enableSharingMutation = useMutation({
    mutationFn: () => enableTripSharing(tripId!),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['trip', tripId] })
    },
  })

  const disableSharingMutation = useMutation({
    mutationFn: () => disableTripSharing(tripId!),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['trip', tripId] })
    },
  })

  if (tripQuery.isLoading || membersQuery.isLoading || myRoleQuery.isLoading) {
    return <p className="bg-night p-10 text-mist">{t('members.loading')}</p>
  }

  const trip = tripQuery.data
  const members = membersQuery.data ?? []
  const invitations = invitationsQuery.data ?? []

  return (
    <div className="min-h-screen bg-night px-10 py-7">
      <button
        type="button"
        onClick={() => navigate(`/trips/${tripId}/itinerary`)}
        className="mb-3.5 font-sans text-sm text-mist"
      >
        {t('members.backToItinerary')}
      </button>
      <h1 className="font-display text-3xl text-ivory">{t('members.title')}</h1>
      <div className="mt-1.5 font-mono text-xs text-brass-dim">{trip?.title}</div>

      <div className="mt-8 rounded-lg border border-line bg-panel p-6">
        <h2 className="mb-5 font-display text-xl text-ivory">{t('members.roster')}</h2>

        {members.map((member) => (
          <div
            key={member.user_id}
            className="mb-2.5 flex items-center justify-between gap-3 rounded-md border border-line bg-panel-2 px-4 py-3"
          >
            <div>
              <span className="font-sans text-[15px] text-ivory">
                {member.email ?? member.user_id}
              </span>
              {member.user_id === currentUserId && (
                <span className="ml-2 font-mono text-[10px] text-brass-dim uppercase">
                  {t('members.you')}
                </span>
              )}
            </div>

            {member.role === 'owner' ? (
              <span className="font-mono text-[10px] tracking-wide text-brass-dim uppercase">
                {t('members.role.owner')}
              </span>
            ) : canManage ? (
              <div className="flex items-center gap-3">
                <select
                  aria-label={t('members.role.select')}
                  value={member.role}
                  disabled={updateRoleMutation.isPending}
                  onChange={(e) =>
                    updateRoleMutation.mutate({
                      userId: member.user_id,
                      role: e.target.value as 'companion' | 'viewer',
                    })
                  }
                  className="rounded border border-line-strong bg-panel px-2 py-1 font-sans text-xs text-ivory"
                >
                  {INVITATION_ROLES.map((role) => (
                    <option key={role} value={role}>
                      {t(`members.role.${role}`)}
                    </option>
                  ))}
                </select>

                {confirmRemoveUserId === member.user_id ? (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-brass-warm">{t('members.confirmRemove')}</span>
                    <button
                      type="button"
                      onClick={() => setConfirmRemoveUserId(null)}
                      className="font-sans text-xs text-mist"
                    >
                      {t('members.cancel')}
                    </button>
                    <button
                      type="button"
                      onClick={() => removeMutation.mutate(member.user_id)}
                      disabled={removeMutation.isPending}
                      className="font-sans text-xs font-medium text-brass-warm"
                    >
                      {t('members.confirmRemoveButton')}
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => setConfirmRemoveUserId(member.user_id)}
                    className="font-sans text-xs text-mist hover:text-brass-warm"
                  >
                    {t('members.remove')}
                  </button>
                )}
              </div>
            ) : (
              <span className="font-mono text-[10px] tracking-wide text-brass-dim uppercase">
                {t(`members.role.${member.role}`)}
              </span>
            )}
          </div>
        ))}

        {canManage && invitations.length > 0 && (
          <>
            <h2 className="mt-6 mb-5 font-display text-xl text-ivory">{t('members.pending')}</h2>
            {invitations.map((invitation) => (
              <div
                key={invitation.id}
                className="mb-2.5 flex items-center justify-between gap-3 rounded-md border border-line-strong bg-panel-2 px-4 py-3"
              >
                <div>
                  <span className="font-sans text-[15px] text-ivory">{invitation.email}</span>
                  <span className="ml-2 font-mono text-[10px] tracking-wide text-brass-dim uppercase">
                    {t(`members.role.${invitation.role}`)}
                  </span>
                </div>

                {confirmRevokeId === invitation.id ? (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-brass-warm">{t('members.confirmRevoke')}</span>
                    <button
                      type="button"
                      onClick={() => setConfirmRevokeId(null)}
                      className="font-sans text-xs text-mist"
                    >
                      {t('members.cancel')}
                    </button>
                    <button
                      type="button"
                      onClick={() => revokeMutation.mutate(invitation.id)}
                      disabled={revokeMutation.isPending}
                      className="font-sans text-xs font-medium text-brass-warm"
                    >
                      {t('members.confirmRevokeButton')}
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => setConfirmRevokeId(invitation.id)}
                    className="font-sans text-xs text-mist hover:text-brass-warm"
                  >
                    {t('members.revoke')}
                  </button>
                )}
              </div>
            ))}
          </>
        )}

        {canManage && (
          <form
            onSubmit={handleSubmit((values) => inviteMutation.mutate(values))}
            className="mt-6 flex flex-col gap-3 rounded-md border border-line-strong bg-panel-2 p-4"
          >
            <input
              type="email"
              aria-label={t('members.email')}
              placeholder={t('members.email')}
              className="rounded border border-line-strong bg-panel px-3 py-2 text-ivory"
              {...register('email')}
            />
            {errors.email && (
              <p role="alert" className="text-sm text-brass-warm">
                {t('members.emailInvalid')}
              </p>
            )}
            <select
              aria-label={t('members.role.select')}
              className="rounded border border-line-strong bg-panel px-3 py-2 text-ivory"
              {...register('role')}
            >
              {INVITATION_ROLES.map((role) => (
                <option key={role} value={role}>
                  {t(`members.role.${role}`)}
                </option>
              ))}
            </select>
            {inviteMutation.isError && (
              <p role="alert" className="text-sm text-brass-warm">
                {t('members.inviteError')}
              </p>
            )}
            <button
              type="submit"
              disabled={inviteMutation.isPending}
              className="rounded border border-brass bg-brass px-4 py-2 font-sans text-sm font-medium text-night disabled:opacity-50"
            >
              {t('members.invite')}
            </button>
          </form>
        )}
      </div>

      {canManage && (
        <div className="mt-8 rounded-lg border border-line bg-panel p-6">
          <h2 className="mb-3 font-display text-xl text-ivory">{t('members.publicShare')}</h2>
          <p className="mb-4 text-sm text-mist">{t('members.publicShareHint')}</p>

          {trip?.share_token ? (
            <div className="flex flex-col gap-3">
              <div className="flex flex-wrap items-center gap-3">
                <input
                  readOnly
                  aria-label={t('members.shareLink')}
                  value={`${window.location.origin}/share/${trip.share_token}`}
                  onFocus={(e) => e.target.select()}
                  className="min-w-[280px] flex-1 rounded border border-line-strong bg-panel-2 px-3 py-2 font-mono text-xs text-ivory"
                />
                <button
                  type="button"
                  onClick={async () => {
                    await navigator.clipboard.writeText(
                      `${window.location.origin}/share/${trip.share_token}`,
                    )
                    setLinkCopied(true)
                    setTimeout(() => setLinkCopied(false), 2000)
                  }}
                  className="rounded border border-line-strong px-3 py-2 font-sans text-xs text-ivory hover:border-brass hover:text-brass"
                >
                  {linkCopied ? t('members.copied') : t('members.copyLink')}
                </button>
              </div>
              <button
                type="button"
                onClick={() => disableSharingMutation.mutate()}
                disabled={disableSharingMutation.isPending}
                className="self-start font-sans text-xs text-mist hover:text-brass-warm"
              >
                {t('members.disableSharing')}
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => enableSharingMutation.mutate()}
              disabled={enableSharingMutation.isPending}
              className="rounded border border-brass bg-brass px-4 py-2 font-sans text-sm font-medium text-night disabled:opacity-50"
            >
              {t('members.enableSharing')}
            </button>
          )}
        </div>
      )}
    </div>
  )
}
